<template>
  <component :is="dynamicComponent"></component>
</template>

<script>
  import { getUrlParams } from '@utilities/mrVueUtils';
  import { mapActions, mapMutations, mapState } from "vuex";
  import { createCustomerFromEmailCapture, addFirstTimeVisitorPhoneAndOffer } from '@services/vueCustomerSvc';

  // Mixins
  import cartMixin from "@mixins/cartMixin";
  import mainAppMixin from "@mixins/mainAppMixin";
  import browserContextMixin from "@mixins/browserContextMixin";

  export default {
    name: "App",

    inject: {
      $ssrContext: { default: () => ({}) }
    },

    props: {
      innerHtml: {
        type: String,
        required: false,
        default: ''
      },
      computedContentClasses: {
        type: Array,
        required: false,
        default: () => []
      },
      cmsContentId: {
        type: [String, Number],
        required: false,
        default: null
      }
    },

    serverPrefetch() {
      // Set the global SSR app flag for making conditional decisions on components logic
      this.setIsVueSSRApp(true);
      this.setCdataUserSettings();

      if (this.$ssrContext.simpleHtml) {
        this.setSimpleHtml(this.$ssrContext.simpleHtml);
      }

      if (this.$ssrContext.userAgentBreakpoint) {
        this.ssrSetBreakpoint(this.$ssrContext.userAgentBreakpoint);
      }
    },

    computed: {
      ...mapState('global', [
        'simpleHtml',
        'appHtml',
        'MRConfig'
      ]),

      fullSsrContentClasses() {
        const ssrClasses = this.computedContentClasses || this.$ssrContext?.computedContentClasses || [];
        return [...ssrClasses, 'background-white'];
      },

      contentId() {
        return this.cmsContentId || this.$ssrContext?.cmsContentId || null;
      },

      htmlContent() {
        // Use innerHtml prop if provided, otherwise fallback to SSR context or appHtml
        if (this.innerHtml) {
          return this.innerHtml;
        }
        return import.meta.env.SSR ? (this.$ssrContext?.html || '') : this.appHtml;
      },

      // Dynamically creates a component options object using the innerHtml prop as its template.
      dynamicComponent() {
        const self = this; // Capture outer 'this' context for use inside inner definitions
        return {
          name: 'DynamicInnerComponent',

          template: `
      <div :class="{'no-scroll': windowData.noScroll || appModalVisible}" @click="setTopNavSelected(0)">
        <a class="skip-to-content-link" href="#content" tab-index="0">Skip to Main Content</a>
        ${self.simpleHtml ? '' : `
          <SiteMessageBanner v-if="!hideSitewideBanner" topics="sitewide"></SiteMessageBanner>
          <SiteMessageBannerCarousel v-if="isAppMounted" :topics="siteMessageBannerCarouselTopics" :use-utm-topic="true"></SiteMessageBannerCarousel>
          <MrNavigation v-if="!isApp" :using-sticky-promo-drawer="!hideStickyPromoDrawer"></MrNavigation>
        `}

        <Suspense>
          <div id="content" class="${self.fullSsrContentClasses.join(' ')}" :class="{'is-mounted': isAppMounted}" data-cms-cid="${self.contentId}">
            ${self.simpleHtml ? '' : `
              <Breadcrumbs v-if="breadcrumbs && isAppMounted" :fragments="breadcrumbs"></Breadcrumbs>
            `}
            ${self.simpleHtml ? '' : `
              <StickyPromoDrawer v-if="!isApp && isAppMounted && !hideStickyPromoDrawer"></StickyPromoDrawer>
            `}

            ${self.htmlContent}
          </div>
        </Suspense>


        ${self.simpleHtml ? '' : `
          <MrFooter v-if="!isApp && isAppMounted"
            :simple-footer="simpleFooter"
            :removeFaqsAndTerms="removeFaqsAndTerms"
            :changeFaqsAndTerms="changeFaqsAndTerms"
            class="${self.fullSsrContentClasses.join(' ')}">
          </MrFooter>

          <ChatWidget v-if="showChatWidget"></ChatWidget>
          <AppModal v-if="isAppMounted"></AppModal>
          <EmailCaptureModal v-if="isAppMounted"></EmailCaptureModal>
          <PhoneCaptureModal v-if="isAppMounted"></PhoneCaptureModal>
          <Notifications v-if="isAppMounted"></Notifications>
        `}
      </div>
          `,

          mixins: [
            cartMixin,
            mainAppMixin,
            browserContextMixin,
          ],

          data: () => ({
            isAppMounted: false,
          }),

          computed: {
            ...mapState('customer', [
              'info',
              'cdata',
            ]),

            ...mapState('global', [
              'urlParams',
              'isApp',
              'headerClass',
              'showHamburgerMenu',
              'hideLogin',
              'removeFaqsAndTerms',
              'changeFaqsAndTerms',
              'simpleFooter',
              'showNavs',
              'appHtml',
              'breadcrumbs',
              'simpleHtml',
              'windowData'
            ]),

            showChatWidget() {
              return this.isAppMounted && !this.isApp;
            },

            customerInfo() {
              return this.$store.state.customer.info;
            },

            hideSitewideBanner() {
              return import.meta.env.SSR ? false : window.__CONTENT_HIDE_SITEWIDE_BANNER__;
            },

            hideStickyPromoDrawer() {
              return import.meta.env.SSR ? true : window.__CONTENT_HIDE_STICKY_PROMO_DRAWER__;
            },

            siteMessageBannerCarouselTopics() {
              return import.meta.env.SSR ? [] : window.__CONTENT_SITE_MESSAGE_TOPICS__;
            },

            appModalVisible() {
              return this.$store.state.modal.visible;
            },

            inColorAdvisorEmailCaptureExperiment() {
              return this.experiments && this.experiments["Advisor Email Capture 2022"] === "B";
            },
          },

          created() {
            // Set the global SSR app flag for making conditional decisions on components logic
            this.setIsVueSSRApp(true);
          },

          mounted() {
            this.loadCustomer();
            this.setCdata(window.cdata);
            this.checkAuth();
            this.runSegmentTracking();
            // TODO: move all logic to the store?
            this.setUrlParams(getUrlParams());
            this.setDeviceSizeSettings();
            this.checkEmailCaptureModal();
            this.initCoupons();

            this.isAppMounted = true;
            this.updateCartFromCookies();

            const { utm_partial_type, utm_partial } = this.urlParams;
            if (utm_partial_type && utm_partial) {
              if (utm_partial_type === 'modal') {
                this.showPartialModal(utm_partial);
              }
            }
            this.setExperiments(window.experiments || {});
            window.trackExperimentViewed = this.mix_trackExperimentViewed;

            window.addToCart = this.addToCart;
            window.notifySuccess = this.notifySuccess;
            window.createCustomerFromEmailCapture = createCustomerFromEmailCapture;
            window.addFirstTimeVisitorPhoneAndOffer = addFirstTimeVisitorPhoneAndOffer;
            window.openQuickLookModalWithProductId = this.openQuickLookModalWithProductId;
          },

          methods: {
            ...mapActions(['notify', 'notifySuccess']),
            ...mapActions('cart', ['addToCart', 'updateCartFromCookies']),
            ...mapActions('customer', ['loadCustomer', 'refreshCustomerCdata']),
            ...mapMutations('customer', ['setCdata']),

            ...mapMutations('global', [
              'setIsVueSSRApp',
              'setUrlParams',
              'setSimpleHtml',
              'setExperiments'
            ]),

            ...mapMutations('siteNav', [
              'setTopNavSelected',
              'setTopNavMobileNavOpen',
            ]),

            ...mapActions('emailCapture', ['runEmailCapture']),

            ...mapActions('partial', [
              'showPartialModal',
            ]),

            ...mapActions('coupons', [
              'initCoupons'
            ]),

            ...mapActions('quickLook', [
              'openQuickLookModalWithProductId'
            ]),

            checkEmailCaptureModal() {
              if (this.inColorAdvisorEmailCaptureExperiment) {
                return;
              }

              if (!this.urlParams['extole_zone_name']) {
                this.runEmailCapture();
              }
            },
          },
        };
      },
    },

    mounted() {
      this.setMRConfig(window.MR.config);
    },

    methods: {
      ...mapMutations('global', [
        'setSimpleHtml',
        'setMRConfig',
        'setIsVueSSRApp',
      ]),

      /**
       * SSR only: takes real user based data injected by the back-end and sets it as CDATA in Vuex for rendering the right
       * components based on CDATA attributes such as "visitorType" and "bestColorMatch"
       * @see https://madison-reed.atlassian.net/wiki/spaces/DOTCOM/pages/979992588/How+to+manage+user-based+data+components+in+SSR+for+above+the+fold+content
       * @see htmlRenderer.js check where the function "serverSideRenderVueContent" is used
       */
      setCdataUserSettings() {
        if (import.meta.env.SSR && this.$ssrContext.userBasedCData) {
          this.$store.commit('customer/setCdata', this.$ssrContext.userBasedCData);
        }
      },
    },
  };
</script>

<style scoped>
.skip-to-content-link {
  position: absolute;
  left: -999px;
  width: 1px;
  height: 1px;
  top: auto;
  overflow: hidden;
  z-index: -999;
}
.skip-to-content-link:focus {
  left: auto;
  top: auto;
  width: auto;
  height: auto;
  overflow: auto;
  margin: 10px;
  padding: 5px;
  background-color: #fff;
  color: #000;
  z-index: 999;
  border: 1px solid #ccc;
}
</style>
